<template>
  <div class="max-w-3xl" v-if="page">
    <div class="adthrive-header min-h-[280px] overflow-hidden"></div>
    <Ad mb-4 ad-slot="6966721074" />
    <h1 class="font-slab text-2xl text-brand-orange">{{ page.title }}</h1>
    <div space-y-10 mt-4>
      <LocationLookup v-model="selectedLocation" />
      <div space-y-4>
        <div v-if="highestPeak || nearestPeak" grid grid-cols-2 gap-6>
          <div v-if="highestPeak">
            <h2 text-2xl font-semibold text-stone-500>Highest Peak</h2>
            <Peak :peak="highestPeak" />
          </div>
          <div v-if="nearestPeak">
            <h2 text-2xl font-semibold text-stone-500>Nearest Peak</h2>
            <Peak :peak="nearestPeak" />
          </div>
        </div>
        <Ad ad-slot="1948746426" />
        <h2 text-2xl font-semibold text-stone-500>Nearby Mountains Map</h2>
        <div w-full h-lg bg-stone-50 bg-opacity-50>
          <LazyMapPeaks
            v-if="peaks?.data"
            :geojson="peaks.data"
            :location="addressLocation"
            @markerMove="onMarkerMove"
          />
        </div>
        <Ad ad-slot="3658187561" />
        <h2 text-2xl font-semibold text-stone-500>Nearby Peaks List</h2>
        <div grid grid-flow-dense sm:grid-cols-2 gap-2 v-if="peaks?.data">
          <template v-for="peak in displayPeaks" :key="peak.id">
            <Peak :peak="peak" />
          </template>
        </div>
        <div flex justify-center p-5 v-if="peaks && !showAll">
          <Button @click="showAll = true">View All</Button>
        </div>
      </div>
      <div space-y-4 v-if="peaks && selectedLocation?.module === 'currentLocation'">
        <div text-stone-5 prose v-if="isLocationSupported">
          <p v-if="!loading && peaks?.error && !isBot()">An error occurred. Please try again later.</p>
          <p v-else-if="!loading && !displayPeaks && !isBot()">
            Sorry, we could not find any mountain peaks near your location.
          </p>
          <p>
            By default, the site will gather the GPS coordinates directly from your device and then uses that
            information to lookup information about where you are.
          </p>
        </div>
      </div>
      <WikipediaInfo v-if="selectedLocation?.name" :slug="selectedLocation.name" />
      <div class="prose lg:text-lg">
        <h2 text-3xl font-semibold text-stone-500>{{ page?.subtitle || "What is this?" }}</h2>
        <ContentRenderer v-if="page" :value="page" />
      </div>
    </div>
  </div>
</template>
<script setup>
const props = defineProps({
  path: {
    type: String,
    default: "address",
  },
});
const router = useRouter();
const route = useRoute();
const { location, updateLocation, isLocationSupported } = useLocation();

const peaks = ref();
const loading = ref(true);
const selectedLocation = ref();
const showAll = ref(false);

const displayPeaks = computed(() => {
  if (!peaks.value?.data?.features?.length) {
    return [];
  }
  if (showAll.value) {
    return peaks.value.data.features;
  }
  return peaks.value.data.features.slice(0, 10);
});
const highestPeak = computed(() => {
  if (!peaks.value?.data?.features?.length) {
    return null;
  }
  return peaks.value.data.features.reduce((a, b) => (a.properties.ele > b.properties.ele ? a : b));
});

const nearestPeak = computed(() => {
  if (!peaks.value?.data?.features?.length) {
    return null;
  }
  return peaks.value.data.features.reduce((a, b) => (a.properties.distance < b.properties.distance ? a : b));
});

const addressLocation = computed(() => {
  if (selectedLocation.value?.module === "currentLocation") {
    if (!location.value?.latitude) {
      return null;
    }
    return {
      latitude: location.value.latitude,
      longitude: location.value.longitude,
    };
  }
  if (selectedLocation.value?.location) {
    return selectedLocation.value.location;
  }
  return null;
});

function onMarkerMove(event) {
  selectedLocation.value = {
    name: "Map Location",
    module: "mapLocation",
    location: {
      latitude: event.lat,
      longitude: event.lng,
    },
  };
}

// run on load
function setLocationFromRoute() {
  if (!route.query.lng) {
    // default to current location
    selectedLocation.value = {
      name: "Current Location",
      module: "currentLocation",
    };
    if (!isLocationSupported.value) {
      selectedLocation.value = null;
    }
  } else {
    selectedLocation.value = {
      name: route.query.title ? decodeURI(route.query.title) : "Saved Location",
      module: route.query.title ? "urlLocation" : "savedLocation",
      location: {
        latitude: parseFloat(route.query.lat),
        longitude: parseFloat(route.query.lng),
      },
    };
  }
}

async function update() {
  if (!isNumber(addressLocation.value?.latitude)) {
    return;
  }
  loading.value = true;
  peaks.value = null;
  try {
    peaks.value = await useFetchPeaks(addressLocation.value);
  } catch (error) {
    console.error(error);
  }
  loading.value = false;
}

// user selected a new location
watch(selectedLocation, async () => {
  if (selectedLocation.value == null) {
    return;
  }

  if (selectedLocation.value?.module === "currentLocation") {
    // request permission to access location
    await updateLocation();
    if (!isLocationSupported.value) {
      selectedLocation.value = null;
      enableLocationDisabledDialog();
      return;
    }
  }

  // update page url with new location
  const query = {};
  if (isNumber(addressLocation.value?.latitude)) {
    query.lat = round(addressLocation.value.latitude, 5);
    query.lng = round(addressLocation.value.longitude, 5);
  }
  if (!["currentLocation", "savedLocation"].includes(selectedLocation.value?.module)) {
    query.title = encodeURI(selectedLocation.value.name);
  }
  router.push({ query });
});

// update content when selected location changes
watch(addressLocation, update);

// load page content
const page = await useContent("/peaks");

useSeoMeta({
  title: () => {
    if (route.query.title) {
      return `Mountains near ${decodeURI(route.query.title)} - What is my elevation?`;
    }
    return `Mountains near me - What is my elevation?`;
  },
  description: page.value.subtitle,
});

defineOgImage({
  title: page.value.title,
  subtitle: page.value.subtitle,
});

// load location if present in url
setLocationFromRoute();
</script>
